import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ModeComment, QuestionAnswer } from '@mui/icons-material'
import { Box, Grid, IconButton, TooltipProps, Typography } from '@mui/material'

import { ConceptTag, Screenshot } from '../../../../api/core'
import { AnalystCommentDialog } from '../../../../components/AnalystCommentDialog/AnalystCommentDialog'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import NotAvailableIcon from '../../../../components/NotAvailableIcon/NotAvailableIcon'
import { formatDate } from '../../../../helpers/date'
import { useAnalystReviewsAndCommentsAccessCheck } from '../../../account/hooks/roleHooks'
import { useCurrentUserLanguage } from '../../../account/hooks/useCurrentUserLanguage'
import { isConceptTag, isEnrichedFeature } from '../../../feature/helpers/helpers'
import GameIcon from '../../../game/components/GameIcon/GameIcon'
import { FeatureGame } from '../../../game/types/Game'
import { NewsEntryScreenshotFeature } from '../../../news/types/UnifiedNewsEntry'
import { EnrichedFeature } from '../../types/ImplementationExamples'
import { screenshotsByConceptTagFilter, screenshotsByFeatureFilter } from '../../util/filters'
import { getCombinedScreenshots, screenshotHasRelatedClassifier } from '../../util/screenshotHelpers'
import ScreenshotCard from './ScreenshotCard'
import './ScreenshotPreview.scss'

interface ScreenshotPreviewProps {
  game: FeatureGame
  classifier?: EnrichedFeature | ConceptTag // Feature or ConceptTag used to filter screenshots
  onRemoveFromCollection?: (screenshotId: string) => void
  onSelect: (screenshots: Screenshot[]) => void
  latestVersion?: string
  hideAddToCollection?: boolean
  relatedFeatureScreenshots?: NewsEntryScreenshotFeature[]
  latestScreenshotVersion?: string | undefined
}

const ScreenshotPreview: React.FC<ScreenshotPreviewProps> = ({
  classifier,
  game,
  onRemoveFromCollection,
  onSelect,
  hideAddToCollection,
  relatedFeatureScreenshots,
  latestScreenshotVersion,
}) => {
  const { t } = useTranslation()
  const userLanguage = useCurrentUserLanguage()
  const [analystCommentDialog, setAnalystCommentDialog] = useState(false)
  const hasAnalystReviewsAndCommentsAccessRights = useAnalystReviewsAndCommentsAccessCheck()

  const { artist, icons, resolvedName: name, featureScreenshots, conceptScreenshots } = game

  let renderedScreenshots: Screenshot[] = []

  if (isConceptTag(classifier)) {
    // concept tag
    renderedScreenshots = classifier ? conceptScreenshots?.filter(screenshotsByConceptTagFilter(classifier)) : conceptScreenshots
  } else if (isEnrichedFeature(classifier)) {
    // feature
    renderedScreenshots = classifier ? featureScreenshots?.filter(screenshotsByFeatureFilter(classifier)) : featureScreenshots
  } else {
    renderedScreenshots = getCombinedScreenshots(game)
  }

  const screenshotCount = renderedScreenshots?.length || 0
  if (screenshotCount === 0) {
    return null
  }
  // Let's pick the first one from array as an example for others
  const { gameVersion, url, versionReleaseDate } = renderedScreenshots[0]
  const screenshotId = renderedScreenshots[0].screenshotId || renderedScreenshots[0].id

  const comments = renderedScreenshots.filter(({ commentPublished }) => commentPublished).length
  const commentId = renderedScreenshots.filter(({ commentId }) => commentId)

  const showLatestBadge = !!(
    (latestScreenshotVersion !== undefined && !game.latestFeatureVersion && latestScreenshotVersion === gameVersion) ||
    (game.latestFeatureVersion && game.latestFeatureVersion === gameVersion)
  )

  // A screenshot without a related feature is silently dropped from a collection (the
  // collection view only renders feature groups), so disable the inline "Add to collection"
  // action and explain why instead of letting it disappear (GR-2357).
  const addToCollectionDisabledTooltip = !screenshotHasRelatedClassifier(renderedScreenshots[0])
    ? t('implementations:tooltip_cannot_add_to_collection_without_features')
    : undefined

  const cardActionsChildren = (
    <>
      <Grid container spacing={1} alignItems="center" onClick={() => onSelect(renderedScreenshots)}>
        <Grid item xs={3}>
          <GameIcon gameName={name} size="normal" src={icons[0]} />
        </Grid>
        <Grid item xs={9}>
          <Typography variant="h3" color="primary">
            {name}
          </Typography>
          <Typography variant="body1" className="artist">
            {artist}
          </Typography>
          <Typography variant="body2" className="version" component={'span'}>
            {!gameVersion && <NotAvailableIcon tooltipContent={`${t('common:version')}: ${t('common:not_available_shorthand')}`} />}
            {gameVersion?.length && `${t('common:version')} ${gameVersion}`}
            {!!versionReleaseDate && ` - ${formatDate(versionReleaseDate, { language: userLanguage })}`}
          </Typography>
        </Grid>
      </Grid>
      {!!comments &&
        (screenshotCount === 1 && comments === 1 ? (
          <>
            <GRTooltip content={t('reviews:tooltip_open_analyst_comment')} tooltipProps={{ placement: 'top' } as TooltipProps}>
              <IconButton className="commentIcon" onClick={() => setAnalystCommentDialog(true)}>
                <ModeComment color="primary" />
              </IconButton>
            </GRTooltip>
            <AnalystCommentDialog
              commentId={commentId[0].commentId}
              hasAccessRights={hasAnalystReviewsAndCommentsAccessRights}
              gameName={game.resolvedName}
              open={!!analystCommentDialog}
              onClose={() => setAnalystCommentDialog(false)}
              isScreenshotAnalysisComment
            />
          </>
        ) : comments > 1 ? (
          <QuestionAnswer className="commentIcon" color="primary" />
        ) : (
          <ModeComment className="commentIcon" color="primary" />
        ))}
    </>
  )

  const relatedFeaturesContent =
    relatedFeatureScreenshots && !!relatedFeatureScreenshots.length ? (
      <>
        <Typography variant="h4" fontWeight={'bold'}>
          {t('feature:related_features')}:
        </Typography>
        {relatedFeatureScreenshots.map(({ featureName_translation, choiceName_translation }, index) => {
          return (
            <Box key={`${featureName_translation}-${choiceName_translation}-${index}`}>
              <small>{`${featureName_translation}: `}</small>
              <small style={{ fontWeight: 700 }}>{choiceName_translation}</small>
            </Box>
          )
        })}
      </>
    ) : undefined

  return (
    <ScreenshotCard
      badgeContent={screenshotCount > 1 ? screenshotCount : 0}
      imageUrl={url}
      onImageClick={() => onSelect(renderedScreenshots)}
      showLatestBadge={showLatestBadge}
      onRemove={onRemoveFromCollection}
      showAddToCollection={screenshotCount === 1 && !hideAddToCollection}
      addToCollectionDisabledTooltip={addToCollectionDisabledTooltip}
      screenshotId={screenshotId}
      game={game}
      cardActionsChildren={cardActionsChildren}
      relatedFeatures={relatedFeaturesContent}
      onCardClick={() => onSelect(renderedScreenshots)}
    />
  )
}

export default ScreenshotPreview
